import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

import { Breakpoint, Color, FontWeight, HeaderMenuTheme, ZIndex } from '../../enums';
import { StyledComponent } from '../../interfaces';
import { hexToRgba } from '../../utils';
import { Logo } from '../logo/logo.component';
import { MenuPosition } from '../menuPosition/menuPosition.component';

import { ContainerProps, DesktopMenuPositionProps, InnerContainerProps, MobileMenuProps } from './headerMenu.types';

const ContainerStyles: { [h in HeaderMenuTheme]: FlattenSimpleInterpolation } = {
  [HeaderMenuTheme.SolidBeige]: css`
    background-color: ${Color.Gray200};
  `,
  [HeaderMenuTheme.SolidWhite]: css`
    background-color: ${Color.White};
    box-shadow: 0px 0px 4.375px ${hexToRgba(Color.Black, 0.15)};
  `,
  [HeaderMenuTheme.Transparent]: css`
    background-color: ${Color.Transparent};
  `,
};

export const Wrapper: StyledComponent<'div'> = styled.div``;

export const Container: StyledComponent<'div', ContainerProps> = styled.div<ContainerProps>`
  padding: 0 40px;
  width: 100%;
  display: flex;
  z-index: ${ZIndex.HeaderMenu};
  ${({ $theme }: ContainerProps): FlattenSimpleInterpolation => ContainerStyles[$theme]};
  position: relative;

  @media ${Breakpoint.Tablet} {
    padding: 0 32px;
  }

  @media ${Breakpoint.Mobile} {
    padding: 0 20px;
  }
`;

export const InnerContainer: StyledComponent<'div', InnerContainerProps> = styled.div<InnerContainerProps>`
  padding: 10px 0;
  ${({ $withBottomBorder }: InnerContainerProps): string | false => $withBottomBorder && `
    border-bottom: 1px solid ${Color.Gray300}
  `};
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-content: center;
`;

export const MenuPositions: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
`;

export const IconsContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
`;

export const MobileMenu: StyledComponent<'div', MobileMenuProps> = styled.div<MobileMenuProps>`
  background-color: ${Color.White};
  padding: 44px 20px 32px;
  width: 100%;
  max-width: 512px;
  transition: cubic-bezier(.22,.61,.36,1) .5s;
  transform: translateX(${({ $isVisible }: MobileMenuProps): string => $isVisible ? '0' : '-100%'});
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: ${ZIndex.HeaderMenuMobileBox};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
`;

export const MobileMenuTopHeader: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
`;

export const MobileMenuTitle: StyledComponent<'div'> = styled.div``;

export const SubMenuSection: StyledComponent<'div'> = styled.div`
  padding: 60px 0 0;

  @media ${Breakpoint.SmallMobile} {
    padding: 40px 0 0;
  }
`;

export const BackButton: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MenuPositionWrapper: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  & + & {
    margin-top: 12px;
  }
`;

export const CloseButton: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: rotate(-90deg);

    @media ${Breakpoint.SmallMobile} {
      transform: none;
    }
  }
`;

export const DesktopMenuPosition: StyledComponent<typeof MenuPosition, DesktopMenuPositionProps>
  = styled(MenuPosition)<DesktopMenuPositionProps>`
    font-weight: ${FontWeight.Light};
    font-size: 16px;
    line-height: 20px;
    color: ${({ $color }: DesktopMenuPositionProps): string => $color};
    transition: 0.3s;

    & + & {
      margin-left: 32px;
    }
  `;

export const TopMobilePosition: StyledComponent<typeof MenuPosition> = styled(MenuPosition)`
  font-weight: ${FontWeight.Medium};
  font-size: 30px;
  line-height: 38px;
  letter-spacing: -0.01em;
  color: ${Color.Black};

  & + & {
    margin-top: 12px;
  }
`;

export const BottomMobilePosition: StyledComponent<typeof MenuPosition> = styled(MenuPosition)`
  font-weight: ${FontWeight.Light};
  font-size: 16px;
  line-height: 22px;
  color: ${Color.Black};

  & + & {
    margin-top: 12px;
  }
`;

export const TopMobilePositions: StyledComponent<'div'> = styled.div`
  padding: 60px 0 80px;
  border-bottom: 1px solid ${Color.Gray300};

  @media ${Breakpoint.SmallMobile} {
    padding: 40px 0 118px;
    border-bottom: 0;
  }
`;

export const BottomMobilePositions: StyledComponent<'div'> = styled.div`
  padding-top: 20px;
`;

export const StyledLogo: StyledComponent<typeof Logo> = styled(Logo)`
  width: 98px;
`;

/**
 * Logo z podpisem pod spodem. Kolor ustawiamy TUTAJ, bo tylko nagłówek zna swój motyw
 * (przezroczysty nad ciemnym tłem albo jasny po przewinięciu), a podpis go dziedziczy.
 */
export const LogoArea: StyledComponent<'div', ContainerProps> = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  color: ${({ $theme }: ContainerProps): string => (
    $theme === HeaderMenuTheme.Transparent ? Color.White : Color.Black
  )};
`;

export const LogoButton: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })``;
